import { LINK_PENDING, pickRecipient, recipientLink, type AccessPerson, type OwnerContact } from '@bbbk/schema';

// messages-one-shape — WHO EACH CARD'S WORDS ARE FOR, and the link line.
//
// The person is `pickRecipient`'s (the sender; else the newest sign-in; else
// the first registered; else the first contact; nobody without a phone while
// someone has one), greeted by the first name the desk typed and rung on their
// own number. Their link is the live one, or the placeholder and a way to make
// one when Patricia taps WhatsApp — never made because a slot drew.
export interface MessageTo {
  first: string;
  phone: string | null;
  link: string | null;
  issueLink?: () => Promise<string | null>;
  /** Whether a sign-in is this person's — who they are, not their first name (PR #275 review). */
  sentByRecipient?: (email: string) => boolean;
}

export function messageTo(args: {
  people: AccessPerson[] | null;
  contacts: readonly OwnerContact[];
  sender: string | null;
  origin: string;
  issueLink: (contactId: number) => Promise<string | null>;
}): MessageTo {
  const firstOf = (c: OwnerContact | undefined, fallback: string) => c?.firstName || c?.name.split(' ')[0] || fallback;
  // NOT READY UNTIL APP ACCESS IS KNOWN (PR #267 review): while it loads, or when its read failed for a
  // household that has people, the person is unknown — so no number, and WhatsApp stays off rather
  // than opening the first contact's thread with the wrong words or no link.
  const unknown = args.people === null || (args.people.length === 0 && args.contacts.length > 0);
  const p = unknown ? null : pickRecipient(args.people!, args.sender);
  if (!p) {
    const c = args.contacts[0];
    return { first: firstOf(c, 'the customer'), phone: null, link: null, issueLink: undefined };
  }
  const contact = args.contacts.find((c) => c.id === p.contactId);
  const { url, issue } = recipientLink(p, args.origin);
  return {
    first: firstOf(contact, p.name.split(' ')[0] || 'the customer'),
    phone: p.phone,
    link: url ?? (issue ? LINK_PENDING : null),
    issueLink: issue ? () => args.issueLink(p.contactId) : undefined,
    sentByRecipient: (email) => {
      const e = email.trim().toLowerCase();
      return (p.usedByEmail ?? '').toLowerCase() === e || (p.signInEmails ?? []).some((s) => s.toLowerCase() === e);
    },
  };
}

/**
 * THE FIRST NAME OF WHOEVER SENT IT (PR #275 review): a sign-in is not always the contact's email —
 * Apple's relay is the case — so the sender is found as `pickRecipient` finds them, through the address
 * their link was bound with or any sign-in of theirs; else (App access still loading, too) the contact with
 * that email; else null.
 */
export function senderFirst(args: { people: AccessPerson[] | null; contacts: readonly OwnerContact[]; email: string }): string | null {
  const e = args.email.trim().toLowerCase();
  const firstOf = (c: OwnerContact | undefined) => (c ? c.firstName || c.name.split(' ')[0] || null : null);
  const p = (args.people ?? []).find((x) => (x.usedByEmail ?? '').toLowerCase() === e || (x.signInEmails ?? []).some((s) => s.toLowerCase() === e));
  if (p) return firstOf(args.contacts.find((c) => c.id === p.contactId)) ?? (p.name.split(' ')[0] || null);
  return firstOf(args.contacts.find((c) => (c.email ?? '').toLowerCase() === e));
}
